import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { Drawer as DrawerPrimitive } from '@base-ui/react/drawer';
import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { cva, type VariantProps } from 'class-variance-authority';
import { XIcon } from 'lucide-react';
import {
    useRef,
    type ComponentPropsWithRef,
    type ComponentType,
    type CSSProperties,
    type ReactElement,
    type ReactNode,
} from 'react';
import { IconButton } from '../candidates/icon-button.tsx';
import { Button, type ButtonVariantProps } from '../primitives/button.tsx';
import { cn } from '../util/classes.ts';
import { useBreakpoint } from '../util/use-breakpoint.tsx';
import { useControllableState } from '../util/use-controllable-state.tsx';
import { Container } from './container.tsx';

type RootPrimitiveProps = Omit<DialogPrimitive.Root.Props, 'onOpenChange'> & {
    onOpenChange?: (open: boolean) => void;
};

type BackdropPrimitiveProps = Omit<DialogPrimitive.Backdrop.Props, 'className' | 'render' | 'style'> & {
    className?: string;
    style?: CSSProperties;
};

type PopupPrimitiveProps = Omit<DialogPrimitive.Popup.Props, 'className' | 'render' | 'style'> & {
    className?: string;
    style?: CSSProperties;
};

type RenderedButtonPrimitiveProps = {
    'data-slot'?: string;
    children?: ReactNode;
    render?: ReactElement;
};

type TitlePrimitiveProps = Omit<DialogPrimitive.Title.Props, 'className' | 'render' | 'style'> & {
    className?: string;
    style?: CSSProperties;
};

export type DialogProps = RootPrimitiveProps & {
    /**
     * Called when the surface closes, whichever path closed it. Shorthand for the
     * `onOpenChange` a controlled dialog would otherwise write to reach the same place;
     * given both, both fire.
     */
    onClose?: () => void;
};

export function Dialog({
    children,
    open,
    defaultOpen = false,
    onOpenChange,
    onClose,
    ...props
}: DialogProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const [isOpen, setOpen] = useControllableState({
        value: open,
        defaultValue: defaultOpen,
        onChange: (nextOpen) => {
            onOpenChange?.(nextOpen);
            if (!nextOpen) {
                onClose?.();
            }
        },
    });
    const RootPrimitive: ComponentType<RootPrimitiveProps> = isDesktop
        ? DialogPrimitive.Root
        : DrawerPrimitive.Root;

    return (
        <RootPrimitive {...props} open={isOpen} onOpenChange={setOpen}>
            {children}
        </RootPrimitive>
    );
}

export type DialogTriggerProps = ComponentPropsWithRef<'button'> &
    ButtonVariantProps & {
        render?: ReactElement;
    };

export function DialogTrigger({ render, children, ...props }: DialogTriggerProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const TriggerPrimitive: ComponentType<RenderedButtonPrimitiveProps> = isDesktop
        ? DialogPrimitive.Trigger
        : DrawerPrimitive.Trigger;

    return (
        <TriggerPrimitive data-slot="dialog-trigger" render={render ?? <Button {...props} />}>
            {children}
        </TriggerPrimitive>
    );
}

export type DialogPortalProps = DialogPrimitive.Portal.Props;

export function DialogPortal({ children, ...props }: DialogPortalProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const PortalPrimitive: ComponentType<DialogPrimitive.Portal.Props> = isDesktop
        ? DialogPrimitive.Portal
        : DrawerPrimitive.Portal;

    return (
        <PortalPrimitive data-slot="dialog-portal" {...props}>
            {children}
        </PortalPrimitive>
    );
}

export type DialogOverlayProps = BackdropPrimitiveProps & { variant?: DialogVariant };

export function DialogOverlay({ className, variant = 'modal', ...props }: DialogOverlayProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const BackdropPrimitive: ComponentType<BackdropPrimitiveProps> = isDesktop
        ? DialogPrimitive.Backdrop
        : DrawerPrimitive.Backdrop;

    return (
        <BackdropPrimitive
            data-slot="dialog-overlay"
            data-variant={variant}
            // Base UI drops the backdrop of a nested surface, so a picker opened
            // from inside a dialog would sit on an undimmed card. Every surface
            // here carries its own scrim; `picker` keeps the lighter one.
            forceRender
            className={cn(
                'fixed inset-0 z-50',
                'bg-scrim/40 data-[variant=picker]:bg-scrim/20',
                'transition-opacity ease-standard duration-interactive',
                'data-ending-style:duration-state',
                'data-starting-style:opacity-0 data-ending-style:opacity-0',
                className,
            )}
            {...props}
        />
    );
}

const dialogContentVariants = cva(
    [
        'pointer-events-auto relative z-10 flex w-full min-h-0 flex-col overflow-hidden',
        'sm:h-auto sm:max-h-full sm:max-w-[40rem] sm:rounded-2xl sm:border sm:border-line-weak',
        'bg-container text-content outline-none',
        'transition-[transform,scale,opacity] ease-standard duration-interactive',
        'data-ending-style:duration-state',
        'data-starting-style:opacity-0 data-ending-style:opacity-0',
        'max-sm:[transform:translateY(max(0px,var(--drawer-swipe-movement-y,0px)))]',
        'max-sm:data-swiping:[transform:translateY(max(0px,var(--drawer-swipe-movement-y,0px)))]!',
        'max-sm:data-starting-style:[transform:translateY(1.5rem)]',
        'max-sm:data-ending-style:[transform:translateY(calc(max(0px,var(--drawer-swipe-movement-y,0px))_+_1.5rem))]',
        'sm:data-starting-style:scale-96 sm:data-ending-style:scale-96',
        'shadow-2xl data-[variant=picker]:shadow-lg',
    ],
    {
        variants: {
            snapMode: {
                contain: 'max-h-[calc(100%-2rem)] rounded-t-2xl border-x border-t border-line-weak',
                max: 'h-[calc(100%-2rem)] rounded-t-2xl border-x border-t border-line-weak',
                fullscreen: 'h-full',
            },
        },
        defaultVariants: {
            snapMode: 'contain',
        },
    },
);

export type DialogContentVariantProps = VariantProps<typeof dialogContentVariants>;

export type DialogSnapMode = NonNullable<DialogContentVariantProps['snapMode']>;

/**
 * How much of the page the surface takes over. Every part carries it as
 * `data-variant`, so call sites can hang their own rules off it too.
 */
export type DialogVariant = 'modal' | 'picker';

export type DialogContentProps = PopupPrimitiveProps &
    DialogContentVariantProps & { variant?: DialogVariant };

export function DialogContent({
    className,
    children,
    ref,
    snapMode,
    variant = 'modal',
    ...props
}: DialogContentProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const cardRef = useRef<HTMLDivElement>(null);
    const popupRef = useMergedRefs(ref, cardRef);
    const ViewportPrimitive: ComponentType<DialogPrimitive.Viewport.Props> = isDesktop
        ? DialogPrimitive.Viewport
        : DrawerPrimitive.Viewport;
    const PopupPrimitive: ComponentType<PopupPrimitiveProps> = isDesktop
        ? DialogPrimitive.Popup
        : DrawerPrimitive.Popup;

    return (
        <DialogPortal>
            <DialogOverlay variant={variant} />
            <ViewportPrimitive
                data-variant={variant}
                className={cn(
                    'pointer-events-none fixed inset-0 z-50 flex flex-col justify-end',
                    'sm:flex-row sm:items-start sm:justify-center sm:px-8 sm:pb-8',
                    'sm:pt-8 sm:data-[variant=picker]:pt-[12vh]',
                )}
            >
                <Container
                    level={1}
                    render={
                        <PopupPrimitive
                            data-slot="dialog-content"
                            data-variant={variant}
                            ref={popupRef}
                            initialFocus={cardRef}
                            className={cn(dialogContentVariants({ snapMode }), className)}
                            {...props}
                        />
                    }
                >
                    {children}
                </Container>
            </ViewportPrimitive>
        </DialogPortal>
    );
}

export type DialogHeaderProps = ComponentPropsWithRef<'div'>;

export function DialogHeader({ className, ...props }: DialogHeaderProps): ReactElement {
    return (
        <Container
            data-slot="dialog-header"
            className={cn(
                'relative z-10 flex shrink-0 items-center gap-default px-5 py-relaxed sm:px-6',
                'border-b',
                className,
            )}
            {...props}
        />
    );
}

export type DialogBodyProps = ComponentPropsWithRef<'div'>;

export function DialogBody({ className, ...props }: DialogBodyProps): ReactElement {
    return (
        <div
            data-slot="dialog-body"
            className={cn(
                'min-h-0 flex-1 overflow-auto overscroll-contain px-5 pt-cozy pb-5 sm:px-6',
                className,
            )}
            {...props}
        />
    );
}

export type DialogFooterProps = ComponentPropsWithRef<'div'>;

export function DialogFooter({ className, ...props }: DialogFooterProps): ReactElement {
    return (
        <div
            data-slot="dialog-footer"
            className={cn(
                'relative z-10 flex shrink-0 flex-row justify-end gap-cozy px-5 py-relaxed sm:px-6',
                'border-t',
                className,
            )}
            {...props}
        />
    );
}

export type DialogTitleProps = ComponentPropsWithRef<'h2'>;

export function DialogTitle({ className, ...props }: DialogTitleProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const TitlePrimitive: ComponentType<TitlePrimitiveProps> = isDesktop
        ? DialogPrimitive.Title
        : DrawerPrimitive.Title;

    return (
        <TitlePrimitive
            data-slot="dialog-title"
            className={cn(
                'min-w-0 flex-1 truncate text-heading-2',
                'in-data-[variant=picker]:text-heading-3',
                className,
            )}
            {...props}
        />
    );
}

export type DialogCloseProps = ComponentPropsWithRef<'button'> & ButtonVariantProps;

export function DialogClose({ className, children, ...props }: DialogCloseProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const ClosePrimitive: ComponentType<RenderedButtonPrimitiveProps> = isDesktop
        ? DialogPrimitive.Close
        : DrawerPrimitive.Close;

    return (
        <ClosePrimitive
            data-slot="dialog-close"
            render={
                <Button className={className} {...props}>
                    {children}
                </Button>
            }
        />
    );
}

export type DialogIconCloseProps = DialogCloseProps & {
    /** Accessible name for the X. Pass the caller's translation of "Close". */
    label?: string;
};

export function DialogIconClose({
    className,
    children,
    label = 'Close',
    ...props
}: DialogIconCloseProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const ClosePrimitive: ComponentType<RenderedButtonPrimitiveProps> = isDesktop
        ? DialogPrimitive.Close
        : DrawerPrimitive.Close;

    return (
        <ClosePrimitive
            data-slot="dialog-close"
            render={
                <IconButton label={label} className={className} {...props}>
                    {children ?? <XIcon className="size-5" />}
                </IconButton>
            }
        />
    );
}
